import React, { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputAdornment, Stack, Typography } from "@mui/material";

import { adminSettingCompanySchemas } from "../../../schemas/adminSettingPage";
import { RFHTextField as RHFTextField } from "../../../components/RFH";
import {
  checkDomain,
  updateTenantInfo,
} from "../../../sevices/apiSystemSettings";
import useMutate from "../../../hooks/useMutate";
import { useResponsive } from "../../../hooks/useResponsive";
import { CustomButton } from "../../../components/Button/Button";

function CompanyInfoForm({ initialData }) {
  const { mutate, isPending } = useMutate(updateTenantInfo, ["tenant-details"]);

  const domainUrl = useMemo(() => window.location.hostname, []);
  const isMobile = useResponsive("down", "md");
  const methods = useForm({
    resolver: yupResolver(adminSettingCompanySchemas),
    defaultValues: initialData,
  });

  const onSubmit = useCallback(
    async (formData) => {
      const domainError = await validateDomain(formData.domain);
      if (domainError) {
        methods.setError("domain", {
          type: "manual",
          message: domainError,
        });
        return;
      }

      if (initialData.domain === formData.domain) {
        mutate({
          company_name: formData.company_name,
        });
      } else {
        mutate(formData);
      }
    },
    [mutate, initialData.domain, methods]
  );

  const validateDomain = useCallback(
    async (domain) => {
      if (/\s/.test(domain)) {
        return "Domain must not contain spaces.";
      }

      if (!/^[a-zA-Z0-9-]+$/.test(domain)) {
        return "Domain must not contain special characters.";
      }

      if (domain && domain !== initialData?.domain) {
        try {
          const res = await checkDomain({ domain });
          const { duplicate_domain } = res.data.data.responseData;

          if (duplicate_domain) {
            return "The domain you selected is already taken.";
          }
        } catch (err) {
          console.error(err);
          return "An error occurred while checking the domain.";
        }
      }

      return null; // No error
    },
    [initialData]
  );

  // Watch form values for real-time changes (optional)
  const formValues = methods.watch();

  // Function to check if any field has a value
  const isAnyFieldFilled = useCallback(() => {
    return Object.values(formValues).some((value, index) => {
      const initialValue = Object.values(initialData)[index];
      return (
        value !== undefined &&
        value !== "" &&
        value !== null &&
        value !== initialValue
      );
    });
  }, [formValues, initialData]);

  return (
    <FormProvider {...methods}>
      <div className={`${isMobile ? "mt-2" : "mt-32"} form-section`}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <React.Fragment>
            <Typography className="setting-heading mb-4 ml-0">
              Company Info
            </Typography>
            <Stack direction={isMobile ? "column" : "row"} spacing={2.5}>
              <RHFTextField
                name="company_name"
                label="Name"
                defaultPadding={false}
                className="company-info-form-text-field"
              />
              <RHFTextField
                label="URL"
                name="domain"
                onBlur={(e) => validateDomain(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="domain-static">
                      .{domainUrl.split(".").splice(1).join(".")}
                    </InputAdornment>
                  ),
                }}
                defaultPadding={false}
                className="company-info-form-text-field"
                disabled
              />
              <RHFTextField
                name="email"
                label="E-mail address"
                defaultPadding={false}
                className="company-info-form-text-field"
              />
            </Stack>
          </React.Fragment>

          <Stack alignItems="end" className="mt-32">
            <CustomButton
              variant="contained"
              type="submit"
              className="theme-bg-color company-info-form-update-button"
              disabled={isPending || !isAnyFieldFilled()}
            >
              Update
            </CustomButton>
          </Stack>
        </form>
      </div>
    </FormProvider>
  );
}

export default React.memo(CompanyInfoForm);
